// M6.3: pure, offline-testable pieces of scripts/mainnet-canary.ts
// (docs/07-mainnet-canary-runbook.md). No node:fs, no stdin, no process.exit,
// no network access, so test/canary.test.ts can drive these directly. The
// script glues these to Rpc, VaultSigner and FileLedgerStore for the parts
// that do touch the network or the filesystem.

import { address } from '@solana/kit';
import { type AccountMetaSpec, type ExecHeaderSpec, executeAccounts, executeInstructionData } from './digest.js';
import { WSOL_MINT, createAtaIdempotent } from './jupiter.js';
import { type Instruction } from './v1.js';
import { addressBytes } from './vault.js';

/** Defined with the fee-side mints in ./jupiter.ts; re-exported for the canary's callers. */
export { USDC_MINT, USDC_MINT_ADDRESS } from './jupiter.js';

export const TOKEN_PROGRAM_ADDRESS = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
export const UPGRADEABLE_LOADER_ADDRESS = 'BPFLoaderUpgradeab1e11111111111111111111111';
/** SIMD-0385, active on mainnet and devnet (docs/01-architecture.md section 11). */
export const FEATURE_TXV1_ADDRESS = 'txv1aq4pp281K9um3tnPgkfX8UqtFT6wcVW3hNezGLL';
/** `treasuryAddress(programId)` for the default program id (docs/01-architecture.md section 7.7); the script asserts this equality at start. */
export const EXPECTED_TREASURY_ADDRESS = '3VXKr5pcfq8D7nfphRHAt6z8oS7RZHCcNRLrqyxTzo1D';

export const TOKEN_PROGRAM: Uint8Array = addressBytes(address(TOKEN_PROGRAM_ADDRESS));

/** True if `rpcUrl`'s host names a mainnet endpoint; used to refuse `--rehearsal` on mainnet (runbook section 6, "never run the canary with --rehearsal against mainnet") and to require a mainnet host for a real run. */
export function isMainnetHost(rpcUrl: string): boolean {
  let host: string;
  try {
    host = new URL(rpcUrl).hostname;
  } catch {
    host = rpcUrl;
  }
  return host.toLowerCase().includes('mainnet');
}

/** SPL Token instruction 17 (SyncNative): data = [17], one writable account, no signer — permissionless, must be top-level (docs/01-architecture.md section 7.6: a `LAMPORT_MOVE` + *inner* SyncNative on the same account is rejected on chain). */
export function syncNativeInstruction(account: Uint8Array): Instruction {
  return { programId: TOKEN_PROGRAM, accounts: [{ pubkey: account, isSigner: false, isWritable: true }], data: Uint8Array.of(17) };
}

/** The Execute header of the wrap step (runbook section 5, step 3): one aux account (the vault's wSOL ATA), `LAMPORT_MOVE` of `swapLamports` to it, no inner instructions. */
export function wrapHeader(validUntilSlot: bigint, swapLamports: bigint): ExecHeaderSpec {
  return { numAux: 1, validUntilSlot, lamportMove: { auxIndex: 0, lamports: swapLamports } };
}

/** The wrap step's sole aux account: the vault's wSOL ATA, writable, not a signer. */
export function wrapAux(wsolAta: Uint8Array): AccountMetaSpec[] {
  return [{ pubkey: wsolAta, isSigner: false, isWritable: true }];
}

/**
 * The wrap transaction's three instructions (docs/01-architecture.md section
 * 7.6, runbook section 5 step 3): create the vault's wSOL ATA (idempotent),
 * the Execute instruction built from a signed wrap authorization, then a
 * top-level SyncNative on the same ATA — never an inner one. `auth` needs
 * only the two fields `signer.authorize()` returns that this assembly uses;
 * the same shape lets tests build a placeholder authorization without a real
 * `VaultSigner`.
 */
export function wrapTransactionInstructions(programId: Uint8Array, relayer: Uint8Array, vault: Uint8Array, wsolAta: Uint8Array, auth: { accounts: AccountMetaSpec[]; instructionData: Uint8Array }): Instruction[] {
  return [createAtaIdempotent(relayer, vault, wsolAta, WSOL_MINT, TOKEN_PROGRAM), { programId, accounts: auth.accounts, data: auth.instructionData }, syncNativeInstruction(wsolAta)];
}

/** Build the same `{accounts, instructionData}` shape `VaultSigner.authorize()` returns, from a header and a signature, without a signer — for offline tests only. */
export function fakeWrapAuthorization(vault: Uint8Array, wsolAta: Uint8Array, header: ExecHeaderSpec, signature: Uint8Array): { accounts: AccountMetaSpec[]; instructionData: Uint8Array } {
  const aux = wrapAux(wsolAta);
  return { accounts: executeAccounts(vault, aux, []), instructionData: executeInstructionData(signature, header, []) };
}

/** `VaultError::SettleFailed` (program/src/lib.rs), the error the deliberately failing swap must produce. */
export const SETTLE_FAILED_CODE = 14;
/** Execute is the fourth instruction of the swap body (vault out ATA, vault in ATA, treasury fee ATA, Execute). */
export const EXECUTE_INDEX_IN_SWAP_BODY = 3;

/**
 * True if `err` (a simulateTransaction / getSignatureStatuses error value) is
 * the vault's settlement rejection at the Execute instruction of the swap
 * body: `{ InstructionError: [3, { Custom: 14 }] }`. Anything else (a Jupiter
 * slippage error because the market moved, a stale blockhash, a missing
 * account) is not the evidence the runbook's step 5 is after.
 */
export function isSettleFailed(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const ie = (err as { InstructionError?: unknown }).InstructionError;
  if (!Array.isArray(ie) || ie.length !== 2) return false;
  const [index, inner] = ie as [unknown, unknown];
  if (index !== EXECUTE_INDEX_IN_SWAP_BODY || !inner || typeof inner !== 'object') return false;
  return (inner as { Custom?: unknown }).Custom === SETTLE_FAILED_CODE;
}

/** `address(programId)` as bytes, for call sites that only have the base58 string. */
export function programIdBytes(programId: string): Uint8Array {
  return addressBytes(address(programId));
}
